import { authApi, ApiError, AuthUser, ValidateResponse } from '../../services/api';
import {
  resolveAuthBootstrapContext,
  getAuthContextMissingMessage,
  AuthBootstrapContext,
} from '../../services/sap-context';
import { logger } from '../../utils/logger';

interface AuthBootstrapHandlers {
  setAuthenticated: (
    accessToken: string,
    expiresIn: number,
    user: AuthUser,
    sessionToken?: string
  ) => void;
}

export type ValidateSessionOutcome =
  | { status: 'authenticated' }
  | { status: 'needs_nice_link'; error: string | null }
  | { status: 'continue' };

export async function tryValidateExistingSession(
  handlers: AuthBootstrapHandlers
): Promise<ValidateSessionOutcome> {
  try {
    const validateResponse: ValidateResponse = await authApi.validateSession();
    const user: AuthUser = {
      identityId: validateResponse.data.user.identityId,
      tenantId: validateResponse.data.user.tenantId,
      cxoneAgentId:
        validateResponse.data.user.cxoneAgentId || validateResponse.data.userInfo?.agentId || '',
      username: validateResponse.data.user.username,
      displayName:
        `${validateResponse.data.user.firstName} ${validateResponse.data.user.lastName}`.trim(),
    };

    handlers.setAuthenticated(
      validateResponse.data.accessToken,
      validateResponse.data.expiresIn,
      user,
      validateResponse.data.token
    );
    logger.lifecycle('[Auth] Session validated');
    return { status: 'authenticated' };
  } catch (err) {
    if (err instanceof ApiError && err.code === 'NEEDS_NICE_LINK') {
      return { status: 'needs_nice_link', error: null };
    }

    if (err instanceof ApiError && err.code === 'INVALID_NICE_CREDENTIALS') {
      return { status: 'needs_nice_link', error: err.message };
    }

    logger.debug('[Auth] No valid session, showing NICE sign-in');
    return { status: 'continue' };
  }
}

export async function resolveAuthContextOrThrow(): Promise<AuthBootstrapContext> {
  const authContext = await resolveAuthBootstrapContext();
  if (!authContext) {
    throw new Error(getAuthContextMissingMessage());
  }
  return authContext;
}
